import { useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { Banner, Button, Input, Select } from '@dd/ui';
import { api, errText } from '@/lib/api';
import { adminMay, useAuth } from '@/lib/auth';
import { useGet, usePaged } from '@/lib/hooks';
import { fmtDT, humanize } from '@/lib/format';
import { Avatar, Chip, ErrorBox, Loading, LoadMore, PageHead, Panel, RoleBadge } from '@/components/ui';

interface Row {
  id: string; adminId: string | null; adminName: string; adminEmail: string | null; adminRole: string | null; adminRemoved: boolean;
  action: string; targetType: string; targetId: string | null; targetLabel: string | null; reason: string | null; before: unknown; after: unknown; ip: string | null; at: string; hash: string;
}
interface AdminLite { id: string; name: string; email: string; role: string }

const tone = (a: string): 'bad' | 'warn' | 'ok' | 'info' | 'neutral' => (/(ban|delete|remove|suspend|reject|disable|failed)/.test(a) ? 'bad' : /(warn|grant|gift|transfer|override|category)/.test(a) ? 'warn' : /(approve|unsuspend|unban|login$|enable|create)/.test(a) ? 'ok' : 'neutral');

/** Who did what, with their name and email. Visible to the Super Admin, or to a person the Super Admin has opened it to. */
export function AuditLog() {
  const me = useAuth((s) => s.admin!);
  const [action, setAction] = useState(''); const [target, setTarget] = useState(''); const [adminId, setAdminId] = useState('');
  const uuid = /^[0-9a-f-]{36}$/i;
  const isSuper = me.role === 'super_admin';
  const people = useGet<{ items: AdminLite[] }>('/admins', { removed: '1' }, isSuper);
  const l = usePaged<Row>('/audit-log', { limit: 50, action: action.trim() || undefined, targetId: uuid.test(target.trim()) ? target.trim() : undefined, adminId: adminId || undefined });
  const [open, setOpen] = useState<string | null>(null);
  const [ver, setVer] = useState<{ ok: boolean; checked: number; brokenAt?: string } | null>(null); const [verErr, setVerErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  const verify = async () => { setBusy(true); setVerErr(null); try { setVer(await api.post('/system/audit-verify')); } catch (e) { setVerErr(errText(e)); } finally { setBusy(false); } };
  return (
    <>
      <PageHead title="Audit log" sub="Every admin action, newest first — who did it (name and email), what, to whom, and why. Rows are hash-chained: an edit or deletion breaks the chain."
        right={adminMay(me, 'admin_manage') ? <Button size="sm" variant="outline" icon={<ShieldCheck size={16} />} loading={busy} onClick={() => void verify()}>Verify chain</Button> : undefined} />
      {!isSuper && <Banner tone="info" className="mb-4">The Super Admin has opened the audit log to you.</Banner>}
      {ver && <Banner tone={ver.ok ? 'ok' : 'bad'} className="mb-4">{ver.ok ? `Chain intact — ${ver.checked} rows verified.` : `CHAIN BROKEN at row ${ver.brokenAt} after ${ver.checked} valid rows. Treat as a security incident.`}</Banner>}
      {verErr && <Banner tone="bad" className="mb-4">{verErr}</Banner>}
      <div className="mb-4 grid gap-3 rounded-2xl border border-line/80 bg-white p-4 sm:grid-cols-3">
        <Select label="Admin" value={adminId} onChange={(e) => setAdminId(e.target.value)}>
          <option value="">Everyone</option>
          {(people.data?.items ?? []).map((a) => <option key={a.id} value={a.id}>{a.name} — {a.email.startsWith('removed+') ? 'removed' : a.email}</option>)}
        </Select>
        <Input label="Action starts with" placeholder="e.g. account.ban, plan_grant, admin" value={action} onChange={(e) => setAction(e.target.value)} />
        <Input label="Target ID" placeholder="Customer / shop / admin UUID" value={target} onChange={(e) => setTarget(e.target.value)} />
      </div>
      <Panel className="[&>div]:p-0">
        {l.isLoading ? <Loading /> : l.error ? <ErrorBox error={l.error} retry={() => void l.refetch()} /> : l.items.length === 0 ? <div className="py-12 text-center text-sm text-ink-3">No entries.</div> : (
          <ul className="divide-y divide-line/70">
            {l.items.map((r) => {
              const isOpen = open === r.id;
              return (
                <li key={r.id} className="px-5 py-3.5">
                  <button type="button" className="flex w-full items-start gap-3 text-left" onClick={() => setOpen(isOpen ? null : r.id)}>
                    <Avatar name={r.adminName} size={38} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-bold text-ink">{r.adminName}</span>
                        {r.adminRole && <RoleBadge role={r.adminRole} />}
                        {r.adminRemoved && <Chip tone="bad">removed</Chip>}
                        <span className="text-xs text-ink-3">{r.adminEmail ?? (r.adminRemoved ? 'email released' : r.adminId ? '' : '')}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                        <Chip tone={tone(r.action)}>{humanize(r.action)}</Chip>
                        <span className="text-ink-2">→</span>
                        {r.targetType === 'account' && r.targetId ? <Link className="font-semibold text-brand" to={`/accounts/${r.targetId}`} onClick={(e) => e.stopPropagation()}>{r.targetLabel ?? `customer ${r.targetId.slice(0, 8)}`}</Link>
                          : <span className="font-semibold text-ink-2">{humanize(r.targetType)}{r.targetLabel ? ` · ${r.targetLabel}` : r.targetId ? ` · ${r.targetId.slice(0, 8)}` : ''}</span>}
                      </div>
                      {r.reason && <div className="mt-1 line-clamp-2 text-sm text-ink-2">“{r.reason}”</div>}
                    </div>
                    <div className="shrink-0 text-right text-xs text-ink-3"><div>{fmtDT(r.at)}</div><div className="mt-0.5 font-mono">{r.ip ?? ''}</div></div>
                    {isOpen ? <ChevronDown size={16} className="mt-1 shrink-0 text-ink-3" /> : <ChevronRight size={16} className="mt-1 shrink-0 text-ink-3" />}
                  </button>
                  {isOpen && (
                    <div className="ml-[50px] mt-3 grid gap-3 md:grid-cols-2">
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-xs"><b>Before</b>{'\n'}{JSON.stringify(r.before, null, 1) ?? 'null'}</pre>
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-xs"><b>After</b>{'\n'}{JSON.stringify(r.after, null, 1) ?? 'null'}</pre>
                      <div className="text-xs text-ink-3 md:col-span-2">Hash <code>{r.hash}</code> · row #{r.id}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="pb-3"><LoadMore has={l.hasNextPage} loading={l.isFetchingNextPage} onClick={() => void l.fetchNextPage()} /></div>
      </Panel>
    </>
  );
}
